import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import AppShell from "@/components/AppShell";
import { api } from "@/lib/api";
import {
  Plus, X, ArrowFatUp, ChatCircle, Warning, Trash, ArrowsClockwise, UsersThree,
} from "@phosphor-icons/react";

const CATEGORIES = [
  { key: "",          label: "all",       tint: "var(--brand-ink)"     },
  { key: "wins",      label: "wins",      tint: "var(--bolt-sage)"     },
  { key: "vent",      label: "vent",      tint: "var(--bolt-terracotta)" },
  { key: "advice",    label: "advice",    tint: "var(--bolt-cobalt)"   },
  { key: "salary",    label: "salary",    tint: "var(--bolt-mustard)"  },
  { key: "interview", label: "interview", tint: "var(--bolt-plum)"     },
  { key: "general",   label: "general",   tint: "var(--brand-muted)"   },
];

const CAT_TINT = Object.fromEntries(CATEGORIES.map((c) => [c.key, c.tint]));

function timeAgo(iso) {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export default function Community() {
  const [posts, setPosts] = useState([]);
  const [category, setCategory] = useState("");
  const [myHandle, setMyHandle] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [composing, setComposing] = useState(false);

  const loadPosts = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/community/posts${category ? `?category=${category}` : ""}`);
      setPosts(data || []);
    } catch {
      toast.error("failed to load community feed");
    } finally {
      setLoading(false);
    }
  }, [category]);

  useEffect(() => { loadPosts(); }, [loadPosts]);
  useEffect(() => {
    api.get("/community/handle/mine").then(({ data }) => setMyHandle(data.handle)).catch(() => {});
  }, []);

  const upvote = async (postId) => {
    try {
      const { data } = await api.post(`/community/posts/${postId}/upvote`);
      setPosts((prev) => prev.map((p) => p.id === postId ? { ...p, has_upvoted: data.upvoted, upvote_count: data.upvote_count } : p));
    } catch { toast.error("upvote failed"); }
  };

  const deletePost = async (postId) => {
    if (!window.confirm("delete this post?")) return;
    try {
      await api.delete(`/community/posts/${postId}`);
      setPosts((prev) => prev.filter((p) => p.id !== postId));
      if (selectedId === postId) setSelectedId(null);
      toast.success("deleted");
    } catch { toast.error("delete failed"); }
  };

  return (
    <AppShell title="Community">
      {/* Intro band */}
      <div className="mb-6 card-hard rounded-md p-5 bg-[var(--pastel-yellow)] flex items-start gap-4" data-testid="community-page">
        <UsersThree size={22} weight="fill" className="mt-1 text-[var(--brand-primary)]" />
        <div className="flex-1 min-w-0">
          <p className="label-tiny">/community</p>
          <p className="text-sm text-[var(--brand-ink)]/85 mt-1 leading-relaxed">
            share wins, vent, swap advice. posts are anonymous by default — you show as{" "}
            <b className="font-mono-d px-1.5 py-0.5 rounded bg-white border-[1.5px] border-[var(--brand-ink)]" data-testid="my-handle">
              {myHandle || "…"}
            </b>
            . tick <span className="font-mono-d">&quot;post as me&quot;</span> to use your real name.
          </p>
        </div>
        <button
          className="btn-hard bg-[var(--brand-ink)] text-white rounded-md px-4 py-2.5 flex items-center gap-2 shrink-0"
          onClick={() => setComposing(true)}
          data-testid="new-post-btn"
        >
          <Plus size={16} weight="bold" /> new post
        </button>
      </div>

      {/* Category filter */}
      <div className="mb-6 flex flex-wrap items-center gap-2" data-testid="community-filters">
        {CATEGORIES.map((c) => {
          const active = category === c.key;
          return (
            <button
              key={c.key || "all"}
              onClick={() => setCategory(c.key)}
              data-testid={`filter-${c.key || "all"}`}
              className={`btn-hard rounded-full px-3.5 py-1.5 text-xs font-mono-d transition-transform ${
                active ? "bg-[var(--brand-ink)] text-white" : "bg-white"
              }`}
              style={active ? {} : { borderColor: c.tint }}
            >
              /{c.label}
            </button>
          );
        })}
        <button
          onClick={loadPosts}
          data-testid="community-refresh"
          className="btn-hard bg-white rounded-full px-3 py-1.5 text-xs inline-flex items-center gap-1.5 ml-auto"
          title="refresh"
        >
          <ArrowsClockwise size={12} weight="bold" /> refresh
        </button>
      </div>

      {/* Feed */}
      {loading ? (
        <div className="card-hard rounded-md p-6 text-sm text-[var(--brand-muted)]" data-testid="community-loading">
          loading feed…
        </div>
      ) : posts.length === 0 ? (
        <div className="card-hard rounded-md p-10 text-center" data-testid="community-empty">
          <UsersThree size={32} weight="duotone" className="mx-auto mb-3 text-[var(--brand-primary)]" />
          <h3 className="font-display text-xl font-semibold mb-1">no posts yet in this category</h3>
          <p className="text-sm text-[var(--brand-muted)] max-w-md mx-auto">
            be the first — click <b>new post</b> above.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {posts.map((p) => (
            <PostCard
              key={p.id}
              post={p}
              onOpen={() => setSelectedId(p.id)}
              onUpvote={() => upvote(p.id)}
              onDelete={() => deletePost(p.id)}
            />
          ))}
        </div>
      )}

      {composing && (
        <ComposeModal
          onClose={() => setComposing(false)}
          onCreated={(p) => { setPosts((prev) => [p, ...prev]); setComposing(false); }}
        />
      )}
      {selectedId && (
        <PostDetail
          postId={selectedId}
          onClose={() => { setSelectedId(null); loadPosts(); }}
        />
      )}
    </AppShell>
  );
}

function PostCard({ post, onOpen, onUpvote, onDelete }) {
  const tint = CAT_TINT[post.category] || "var(--brand-muted)";
  return (
    <article
      className="card-hard rounded-md bg-white p-5 flex items-start gap-4 hover:-translate-y-0.5 transition-transform"
      data-testid={`post-card-${post.id}`}
    >
      {/* Upvote column */}
      <div className="flex flex-col items-center gap-1 shrink-0 w-14">
        <button
          onClick={onUpvote}
          data-testid={`upvote-${post.id}`}
          title={post.has_upvoted ? "remove upvote" : "upvote"}
          className={`btn-hard rounded-md px-2.5 py-1.5 text-xs inline-flex items-center gap-1 ${
            post.has_upvoted ? "bg-[var(--brand-primary)] text-white" : "bg-white"
          }`}
        >
          <ArrowFatUp size={12} weight={post.has_upvoted ? "fill" : "bold"} />
          {post.upvote_count}
        </button>
        <button onClick={onOpen} className="text-[10px] font-mono-d text-[var(--brand-muted)] hover:text-[var(--brand-ink)] hover:underline">
          {post.comment_count} {post.comment_count === 1 ? "reply" : "replies"}
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0 cursor-pointer" onClick={onOpen}>
        <div className="flex items-center flex-wrap gap-2 mb-1.5">
          <span
            className="inline-flex items-center text-[10px] font-mono-d uppercase tracking-wider px-2 py-0.5 rounded border-[1.5px] border-[var(--brand-ink)]"
            style={{ background: tint, color: tint === "var(--brand-ink)" || tint === "var(--brand-muted)" ? "white" : "var(--brand-ink)" }}
          >
            /{post.category}
          </span>
          <span className="text-[11px] font-mono-d text-[var(--brand-muted)]">
            by <b className="text-[var(--brand-ink)]">{post.author_handle}</b> · {timeAgo(post.created_at)}
          </span>
          {post.is_own && (
            <button
              onClick={(e) => { e.stopPropagation(); onDelete(); }}
              className="ml-auto btn-hard bg-white rounded p-1 hover:bg-[var(--pastel-peach)]"
              data-testid={`delete-${post.id}`}
              title="delete post"
            >
              <Trash size={12} weight="bold" />
            </button>
          )}
        </div>
        <h3 className="font-display text-lg font-semibold leading-tight mb-1" data-testid="post-title">
          {post.title}
        </h3>
        <p className="text-sm text-[var(--brand-ink)]/80 line-clamp-3 whitespace-pre-wrap">
          {post.body}
        </p>
      </div>
    </article>
  );
}

function ComposeModal({ onClose, onCreated }) {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [category, setCategory] = useState("general");
  const [useRealName, setUseRealName] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (title.trim().length < 5) { toast.error("title needs 5+ chars"); return; }
    if (body.trim().length < 20) { toast.error("body needs 20+ chars"); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/community/posts", { title: title.trim(), body: body.trim(), category, use_real_name: useRealName });
      onCreated(data);
      toast.success("posted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "post failed");
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-start justify-center p-4 md:p-8 overflow-y-auto"
      onClick={onClose}
      data-testid="compose-modal"
    >
      <div
        className="card-hard bg-[var(--brand-bg)] rounded-md w-full max-w-2xl mt-4 md:mt-16 flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5 border-b-[1.5px] border-[var(--brand-ink)] flex items-center justify-between">
          <div>
            <p className="label-tiny">/new post</p>
            <h2 className="font-display text-2xl font-semibold leading-tight mt-0.5">say what you need to say.</h2>
          </div>
          <button
            onClick={onClose}
            data-testid="compose-close"
            className="btn-hard bg-white rounded-md px-2.5 py-1.5 text-xs inline-flex items-center gap-1"
          >
            <X size={12} weight="bold" /> close
          </button>
        </div>

        <div className="p-5 space-y-4">
          <label className="block">
            <span className="label-tiny block mb-1.5">category</span>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              data-testid="compose-category"
              className="w-full card-hard rounded-md px-3 py-2.5 bg-white text-sm"
            >
              {CATEGORIES.filter((c) => c.key).map((c) => (
                <option key={c.key} value={c.key}>/{c.label}</option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="label-tiny block mb-1.5">title <span className="text-[var(--brand-muted)]">· 5–200 chars</span></span>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="quick summary (e.g. 'got the offer!!!')"
              maxLength={200}
              data-testid="compose-title"
              className="w-full card-hard rounded-md px-3 py-2.5 bg-white text-sm"
            />
            <p className="text-[10px] font-mono-d text-[var(--brand-muted)] mt-1">{title.length}/200</p>
          </label>

          <label className="block">
            <span className="label-tiny block mb-1.5">body <span className="text-[var(--brand-muted)]">· 20–5000 chars</span></span>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={10}
              maxLength={5000}
              data-testid="compose-body"
              placeholder="what's on your mind? give context, ask a question, drop the receipts…"
              className="w-full card-hard rounded-md px-3 py-2.5 bg-white text-sm leading-relaxed resize-y min-h-[220px]"
            />
            <p className="text-[10px] font-mono-d text-[var(--brand-muted)] mt-1">{body.length}/5000</p>
          </label>

          <label className="flex items-start gap-2 text-xs cursor-pointer p-3 rounded-md bg-white border-[1.5px] border-[var(--brand-ink)]/20">
            <input
              type="checkbox"
              checked={useRealName}
              onChange={(e) => setUseRealName(e.target.checked)}
              data-testid="compose-real-name"
              className="mt-0.5"
            />
            <span>
              <b>post as me</b> — show my real name.<br/>
              <span className="text-[var(--brand-muted)]">otherwise you appear as your anonymous handle.</span>
            </span>
          </label>
        </div>

        <div className="p-5 border-t-[1.5px] border-[var(--brand-ink)] flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="btn-hard bg-white rounded-md px-4 py-2 text-sm"
          >
            cancel
          </button>
          <button
            className="btn-hard bg-[var(--brand-ink)] text-white rounded-md px-4 py-2 text-sm disabled:opacity-60 inline-flex items-center gap-2"
            disabled={submitting || title.trim().length < 5 || body.trim().length < 20}
            onClick={submit}
            data-testid="compose-submit"
          >
            {submitting ? "posting…" : <>post <Plus size={14} weight="bold" /></>}
          </button>
        </div>
      </div>
    </div>
  );
}

function PostDetail({ postId, onClose }) {
  const [data, setData] = useState(null);
  const [comment, setComment] = useState("");
  const [useRealName, setUseRealName] = useState(false);
  const [posting, setPosting] = useState(false);
  const [reporting, setReporting] = useState(false);

  useEffect(() => {
    api.get(`/community/posts/${postId}`).then((r) => setData(r.data)).catch(() => toast.error("failed to load post"));
  }, [postId]);

  const submitComment = async () => {
    if (comment.trim().length < 3) return;
    setPosting(true);
    try {
      const { data: c } = await api.post(`/community/posts/${postId}/comments`, { body: comment.trim(), use_real_name: useRealName });
      setData((d) => ({ ...d, comments: [...(d?.comments || []), c] }));
      setComment("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "comment failed");
    } finally { setPosting(false); }
  };

  const report = async () => {
    const reason = window.prompt("why are you reporting this post?");
    if (!reason || reason.length < 5) return;
    setReporting(true);
    try {
      await api.post(`/community/posts/${postId}/report`, { reason });
      toast.success("reported — thanks, we'll review");
    } catch { toast.error("report failed"); }
    setReporting(false);
  };

  const deleteComment = async (commentId) => {
    if (!window.confirm("delete this reply?")) return;
    try {
      await api.delete(`/community/comments/${commentId}`);
      setData((d) => ({ ...d, comments: d.comments.filter((c) => c.id !== commentId) }));
    } catch { toast.error("delete failed"); }
  };

  if (!data) return null;
  const p = data.post;
  const tint = CAT_TINT[p.category] || "var(--brand-muted)";
  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-start justify-center p-4 md:p-8 overflow-y-auto"
      onClick={onClose}
      data-testid="post-detail-modal"
    >
      <div
        className="card-hard bg-[var(--brand-bg)] rounded-md w-full max-w-2xl mt-4 md:mt-16 flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5 border-b-[1.5px] border-[var(--brand-ink)]">
          <div className="flex items-center justify-between mb-3">
            <span
              className="inline-flex items-center text-[10px] font-mono-d uppercase tracking-wider px-2 py-0.5 rounded border-[1.5px] border-[var(--brand-ink)]"
              style={{ background: tint, color: tint === "var(--brand-ink)" || tint === "var(--brand-muted)" ? "white" : "var(--brand-ink)" }}
            >
              /{p.category}
            </span>
            <button
              onClick={onClose}
              className="btn-hard bg-white rounded-md px-2.5 py-1.5 text-xs inline-flex items-center gap-1"
            >
              <X size={12} weight="bold" /> close
            </button>
          </div>
          <h2 className="font-display text-2xl font-semibold leading-tight" data-testid="detail-title">{p.title}</h2>
          <p className="text-[11px] font-mono-d text-[var(--brand-muted)] mt-1">
            by <b className="text-[var(--brand-ink)]">{p.author_handle}</b> · {timeAgo(p.created_at)}
          </p>
        </div>

        <div className="p-5 border-b-[1.5px] border-[var(--brand-ink)]/20">
          <p className="text-sm whitespace-pre-wrap leading-relaxed" data-testid="detail-body">{p.body}</p>
          <div className="flex items-center gap-3 mt-4">
            <span className="text-xs font-mono-d inline-flex items-center gap-1 text-[var(--brand-muted)]">
              <ArrowFatUp size={12} weight="fill" /> {p.upvote_count} upvotes
            </span>
            <span className="text-xs font-mono-d inline-flex items-center gap-1 text-[var(--brand-muted)]">
              <ChatCircle size={12} weight="fill" /> {data.comments.length} replies
            </span>
            {!p.is_own && (
              <button
                onClick={report}
                disabled={reporting}
                data-testid="report-btn"
                className="ml-auto btn-hard bg-white rounded-md px-2.5 py-1 text-xs inline-flex items-center gap-1.5 disabled:opacity-60"
              >
                <Warning size={12} weight="bold" /> report
              </button>
            )}
          </div>
        </div>

        <div className="p-5">
          <p className="label-tiny mb-3">/replies ({data.comments.length})</p>
          <div className="space-y-2.5 mb-4">
            {data.comments.length === 0 && (
              <p className="text-sm text-[var(--brand-muted)] italic">no replies yet — be the first.</p>
            )}
            {data.comments.map((c) => (
              <div key={c.id} className="card-hard rounded-md bg-white p-3" data-testid={`comment-${c.id}`}>
                <p className="text-[11px] font-mono-d text-[var(--brand-muted)] uppercase">
                  {c.author_handle} · {timeAgo(c.created_at)}
                </p>
                <p className="text-sm mt-1 whitespace-pre-wrap leading-relaxed">{c.body}</p>
                {c.is_own && (
                  <button
                    onClick={() => deleteComment(c.id)}
                    className="mt-2 text-[10px] font-mono-d text-[var(--bolt-terracotta)] hover:underline"
                  >
                    delete
                  </button>
                )}
              </div>
            ))}
          </div>

          <div className="space-y-2">
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={3}
              maxLength={1000}
              data-testid="comment-body"
              placeholder="add a reply…"
              className="w-full card-hard rounded-md px-3 py-2 bg-white text-sm min-h-[80px] resize-y"
            />
            <label className="flex items-center gap-2 text-xs cursor-pointer">
              <input
                type="checkbox"
                checked={useRealName}
                onChange={(e) => setUseRealName(e.target.checked)}
                data-testid="comment-real-name"
              />
              reply as me (real name — otherwise anonymous handle)
            </label>
            <div className="flex justify-end">
              <button
                className="btn-hard bg-[var(--brand-ink)] text-white rounded-md px-4 py-2 text-sm disabled:opacity-60 inline-flex items-center gap-2"
                disabled={posting || comment.trim().length < 3}
                onClick={submitComment}
                data-testid="comment-submit"
              >
                {posting ? "posting…" : <>reply <ChatCircle size={12} weight="fill" /></>}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
